import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Pagination,
  Chip,
  Menu,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ImageIcon from "@mui/icons-material/Image";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllAdvertisementApi,
  getAdvertisementMetaApi,
  getAdvertisementSummaryApi,
  duplicateAdvertisementApi,
  publishAdvertisementApi,
  pauseAdvertisementApi,
  resumeAdvertisementApi,
  archiveAdvertisementApi,
  deleteAdvertisementApi,
} from "../../../redux/api/api";

const STATUS_COLORS = {
  DRAFT: "default",
  SCHEDULED: "info",
  ACTIVE: "success",
  PAUSED: "warning",
  EXPIRED: "error",
  COMPLETED: "secondary",
  ARCHIVED: "default",
};

const StatusChip = ({ status }) => <Chip size="small" label={status} color={STATUS_COLORS[status] || "default"} variant={status === "ACTIVE" ? "filled" : "outlined"} />;

const SummaryCard = ({ label, value, color }) => (
  <Card sx={{ flex: 1, minWidth: 140 }}>
    <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || "text.primary" }}>
        {value ?? "—"}
      </Typography>
    </CardContent>
  </Card>
);

const AdvertisementList = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  // Mirrors model/Advertisement.js's enums — used until /meta responds, and
  // kept if it never does, so the filter dropdowns are never left empty.
  const [meta, setMeta] = useState({
    services: ["ride", "events", "movies", "food", "hotels", "bus", "train", "wallet", "shopping", "recharge", "other"],
    placements: [
      "HOME_HERO",
      "HOME_TOP_PROMO",
      "HOME_OFFER",
      "HOME_OFFER_FOR_YOU",
      "HOME_SERVICE_PROMO",
      "HOME_BETWEEN_SERVICES",
      "RIDE_PROMOTION",
      "EVENT_PROMOTION",
      "MOVIE_PROMOTION",
    ],
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [placementFilter, setPlacementFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [page, setPage] = useState(1);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCampaign, setMenuCampaign] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'archive'|'delete', campaign }

  const loadMeta = async () => {
    try {
      const [metaRes, summaryRes] = await Promise.all([getAdvertisementMetaApi(), getAdvertisementSummaryApi()]);
      const metaInfo = metaRes?.data?.info;
      if (metaInfo && Object.keys(metaInfo).length) setMeta(metaInfo);
      setSummary(summaryRes?.data?.info || null);
    } catch (error) {
      // Non-fatal — filters keep their built-in fallback list, summary cards just stay blank.
      toast.error(
        error?.response?.status === 402 || error?.response?.status === 401
          ? "Your session has expired — please log in again."
          : error?.response?.data?.message || "Couldn't reach the server for the summary cards / filter options.",
      );
    }
  };

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const response = await getAllAdvertisementApi({
        page,
        limit: pagination.limit,
        search: search || undefined,
        status: statusFilter || undefined,
        serviceId: serviceFilter || undefined,
        placement: placementFilter || undefined,
        sourceType: sourceFilter || undefined,
      });
      const info = response?.data?.info || {};
      setCampaigns(info.campaigns || []);
      setPagination(info.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load campaigns.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(loadCampaigns, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, serviceFilter, placementFilter, sourceFilter]);

  const openMenu = (event, campaign) => {
    setMenuAnchor(event.currentTarget);
    setMenuCampaign(campaign);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCampaign(null);
  };

  const refreshAfterAction = async () => {
    await Promise.all([loadCampaigns(), loadMeta()]);
  };

  const handleDuplicate = async (campaign) => {
    closeMenu();
    try {
      await duplicateAdvertisementApi(campaign._id);
      toast.success("Campaign duplicated as a new draft.");
      refreshAfterAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to duplicate campaign.");
    }
  };

  const handlePublish = async (campaign) => {
    closeMenu();
    try {
      await publishAdvertisementApi(campaign._id);
      toast.success("Campaign published.");
      refreshAfterAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to publish campaign.");
    }
  };

  const handlePause = async (campaign) => {
    closeMenu();
    try {
      await pauseAdvertisementApi(campaign._id);
      toast.success("Campaign paused.");
      refreshAfterAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to pause campaign.");
    }
  };

  const handleResume = async (campaign) => {
    closeMenu();
    try {
      await resumeAdvertisementApi(campaign._id);
      toast.success("Campaign resumed.");
      refreshAfterAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to resume campaign.");
    }
  };

  const confirmArchiveOrDelete = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.type === "archive") {
        await archiveAdvertisementApi(confirmAction.campaign._id);
        toast.success("Campaign archived.");
      } else {
        await deleteAdvertisementApi(confirmAction.campaign._id);
        toast.success("Campaign deleted.");
      }
      setConfirmAction(null);
      refreshAfterAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Action failed.");
      setConfirmAction(null);
    }
  };

  const activeFiltersCount = useMemo(
    () => [statusFilter, serviceFilter, placementFilter, sourceFilter].filter(Boolean).length,
    [statusFilter, serviceFilter, placementFilter, sourceFilter],
  );

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>Advertisements</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>Marketing</li>
          <li>Advertisements</li>
        </ul>
      </div>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <SummaryCard label="Active" value={summary?.active} color="#2e7d32" />
        <SummaryCard label="Scheduled" value={summary?.scheduled} color="#0288d1" />
        <SummaryCard label="Draft" value={summary?.draft} color="#616161" />
        <SummaryCard label="Paused" value={summary?.paused} color="#ed6c02" />
        <SummaryCard label="Expired" value={summary?.expired} color="#d32f2f" />
        <SummaryCard label="Limit Reached" value={summary?.completed} color="#7b1fa2" />
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Campaigns {pagination.total ? `(${pagination.total})` : ""}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/marketing/advertisements/create")}>
              New Campaign
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by name or campaign ID"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {["DRAFT", "SCHEDULED", "ACTIVE", "PAUSED", "EXPIRED", "COMPLETED", "ARCHIVED"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Service</InputLabel>
              <Select
                label="Service"
                value={serviceFilter}
                onChange={(e) => {
                  setServiceFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All Services</MenuItem>
                {(meta.services || []).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Placement</InputLabel>
              <Select
                label="Placement"
                value={placementFilter}
                onChange={(e) => {
                  setPlacementFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All Placements</MenuItem>
                {(meta.placements || []).map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Source</InputLabel>
              <Select
                label="Source"
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All Sources</MenuItem>
                <MenuItem value="IN_APP">In-app</MenuItem>
                <MenuItem value="THIRD_PARTY">Third-party</MenuItem>
              </Select>
            </FormControl>
            {activeFiltersCount > 0 ? (
              <Button
                size="small"
                onClick={() => {
                  setStatusFilter("");
                  setServiceFilter("");
                  setPlacementFilter("");
                  setSourceFilter("");
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            ) : null}
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Placement</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      Loading campaigns…
                    </TableCell>
                  </TableRow>
                ) : campaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      No campaigns found. Try adjusting your filters, or create a new campaign.
                    </TableCell>
                  </TableRow>
                ) : (
                  campaigns.map((campaign) => {
                    const thumb = campaign.creative?.en?.thumbnailUrl || campaign.creative?.en?.imageUrl;
                    const locationLabel =
                      campaign.targeting?.mode === "ALL"
                        ? "All Locations"
                        : campaign.targeting?.mode === "RADIUS"
                          ? `${campaign.targeting?.radius?.radiusKm || "?"} km radius`
                          : [
                              ...(campaign.targeting?.stateIds || []).map((s) => s.name),
                              ...(campaign.targeting?.cityIds || []).map((c) => c.name),
                            ].join(", ") || "Custom";
                    return (
                      <TableRow key={campaign._id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar variant="rounded" src={thumb} sx={{ width: 40, height: 40, bgcolor: "#eee" }}>
                              <ImageIcon fontSize="small" color="disabled" />
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, cursor: "pointer" }}
                                onClick={() => navigate(`/marketing/advertisements/${campaign._id}`)}
                              >
                                {campaign.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {campaign.campaignId}
                                {campaign.analytics?.impressions
                                  ? ` · ${campaign.analytics.impressions.toLocaleString("en-IN")} impr · ${((campaign.analytics.clicks / campaign.analytics.impressions) * 100).toFixed(1)}% CTR`
                                  : ""}
                              </Typography>
                              {campaign.source?.type === "THIRD_PARTY" ? (
                                <Chip size="small" label={campaign.source.advertiser?.name || "Third-party"} sx={{ mt: 0.5, height: 18, fontSize: 11 }} />
                              ) : null}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{campaign.serviceId}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={campaign.placement} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography variant="caption" noWrap display="block">
                            {locationLabel}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {campaign.schedule?.startAt ? new Date(campaign.schedule.startAt).toLocaleDateString() : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            to {campaign.schedule?.endAt ? new Date(campaign.schedule.endAt).toLocaleDateString() : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{campaign.priority}</TableCell>
                        <TableCell>
                          <StatusChip status={campaign.computedStatus || campaign.status} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{new Date(campaign.updatedAt).toLocaleDateString()}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => openMenu(e, campaign)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.totalPages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination count={pagination.totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" />
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => navigate(`/marketing/advertisements/${menuCampaign?._id}`)}>View</MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/marketing/advertisements/${menuCampaign?._id}/edit`, { state: menuCampaign });
            closeMenu();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDuplicate(menuCampaign)}>Duplicate</MenuItem>
        {menuCampaign?.status === "DRAFT" ? <MenuItem onClick={() => handlePublish(menuCampaign)}>Publish</MenuItem> : null}
        {menuCampaign?.status === "ACTIVE" ? <MenuItem onClick={() => handlePause(menuCampaign)}>Pause</MenuItem> : null}
        {menuCampaign?.status === "PAUSED" ? <MenuItem onClick={() => handleResume(menuCampaign)}>Resume</MenuItem> : null}
        {["DRAFT", "PAUSED", "ACTIVE"].includes(menuCampaign?.status) ? (
          <MenuItem
            onClick={() => {
              setConfirmAction({ type: "archive", campaign: menuCampaign });
              closeMenu();
            }}
          >
            Archive
          </MenuItem>
        ) : null}
        {["DRAFT", "ARCHIVED"].includes(menuCampaign?.status) ? (
          <MenuItem
            sx={{ color: "error.main" }}
            onClick={() => {
              setConfirmAction({ type: "delete", campaign: menuCampaign });
              closeMenu();
            }}
          >
            Delete
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog open={Boolean(confirmAction)} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{confirmAction?.type === "delete" ? "Delete campaign?" : "Archive campaign?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction?.type === "delete"
              ? `"${confirmAction?.campaign?.name}" will be permanently deleted. This cannot be undone.`
              : `"${confirmAction?.campaign?.name}" will be archived and stop showing to customers.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button color={confirmAction?.type === "delete" ? "error" : "warning"} variant="contained" onClick={confirmArchiveOrDelete}>
            {confirmAction?.type === "delete" ? "Delete" : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdvertisementList;
